import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  DialogButton,
  Dropdown,
} from '@beeinventor/dasiot-react-component-lib';
import { CircularProgress, styled } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';

import { Locator, LocatorType } from '../../../types/Device';
import { SystemState } from '../../../types/Store';

import { getBleAoas, getDasbeacons, getUwbAoas } from '../../../apis/deviceApi';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { setTransferDeviceMap } from '../../../slices/systemSlice';

import HardwareItem from '../../../components/HardwareItem';

import { locatorTypeList } from '../../../common/device';

const deviceFilterList: Array<{
  id: string;
  name: string;
  value: LocatorType;
}> = [...locatorTypeList];

const Container = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100% - 48px)',
    '& > .filter-container': {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      background: '#c4c4c4',
      padding: '17px 48px',
      gap: '8px',
      '& > .info': {
        flex: '1 1 auto',
        fontWeight: 'bold',
      },
    },
    '& > .content': {
      flex: 1,
      padding: '46px 48px',
      overflow: 'auto',
      '& > .loading': {
        width: 'auto',
        height: 'auto',
      },
    },
    '& > .transfer-container': {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '0.875rem',
      lineHeight: 1.5,
      background: theme.color.secondary.$40,
      padding: '16px',
      borderRadius: '10px 10px 0px 0px',
      margin: '0 48px',
    },
  };
});

const LocatorPage = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const buttomRef = useRef<HTMLDivElement | null>(null);
  const filterText = useAppSelector((store) => store.system.filter.text);
  const [deviceMap, setDasIdMap] = useState<SystemState['transferDeviceMap']>(
    {},
  );
  const [deviceType, setDeviceType] = useState<LocatorType>('dasbeacon');
  const [total, setTotal] = useState(0);

  const {
    data: getLocatorResponse,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isFetching: isFetchingDasEndPoint,
    isSuccess,
  } = useInfiniteQuery({
    queryKey: ['get-locator', deviceType, filterText],
    queryFn: async ({ pageParam }: { pageParam?: string }) => {
      const params = {
        dasId: filterText !== '' ? filterText : undefined,
        nextCursor: pageParam,
        limit: 20,
        order: 'desc',
      };

      switch (deviceType) {
        case 'dasbeacon':
          return getDasbeacons(params);
        case 'ble_aoa':
          return getBleAoas(params);
        case 'uwb_aoa':
        case 'daslocate_u':
          return getUwbAoas(params);
        default:
          throw new Error(`Unsupported device type: ${deviceType}`);
      }
    },
    enabled: location.pathname === '/devices/locator' && !!deviceType,
    getNextPageParam: (res) => res.data.paging.nextCursor,
    refetchOnWindowFocus: false,
    initialPageParam: undefined,
  });

  useEffect(() => {
    if (!isFetchingDasEndPoint && hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingDasEndPoint]);

  useEffect(() => {
    if (isSuccess && getLocatorResponse) {
      setTotal(getLocatorResponse.pages[0].data.metadata.total);
    }
  }, [isSuccess, getLocatorResponse]);

  const deviceItems = useMemo(() => {
    const locators: Locator[] =
      getLocatorResponse?.pages.map((res) => res.data.data).flat() || [];

    return locators
      .filter((locator) => {
        if (deviceType === 'daslocate_u') {
          return locator.dasId.startsWith('AWUA'); // true/false
        }
        if (deviceType === 'uwb_aoa') {
          return locator.dasId.startsWith('ALUA'); // true/false
        }
        return true;
      })
      .map((locator, index) => {
        return (
          <HardwareItem
            key={`device-${locator.id}`}
            number={index + 1}
            data={locator}
            isSelected={deviceMap[locator.dasId]?.isSelected ?? false}
            onSelect={(data, value) => {
              setDasIdMap({
                ...deviceMap,
                [data.dasId]: {
                  id: data.id,
                  isSelected: value,
                },
              });
            }}
          />
        );
      });
  }, [getLocatorResponse, deviceMap, deviceType]);

  const selectedDevicesCount = Object.values(deviceMap).reduce((prev, curr) => {
    if (curr?.isSelected) {
      prev++;
    }
    return prev;
  }, 0);

  return (
    <Container>
      <div className="filter-container">
        <Dropdown
          sx={{
            display: 'inline-flex',
            maxWidth: '220px',
            paddingTop: 0,
            paddingBottom: 0,
          }}
          selectedId={deviceType}
          list={deviceFilterList}
          placeholder="Device Type"
          popperProps={{
            sx: {
              color: '#606060',
            },
          }}
          onSelect={(v) => {
            setDeviceType(v as LocatorType);
          }}
        />
        <div className="info">Total: {total}</div>
        <Button
          sx={{
            '& a': {
              color: '#fff',
              textDecoration: 'none',
            },
          }}
          variant="contained"
          color="primary"
          onClick={() => {
            //
          }}
        >
          <Link to="create">Add Locator</Link>
        </Button>
      </div>
      <div ref={contentRef} className="content">
        {isLoading ? (
          <div className="loading">
            <CircularProgress color="primary" />
          </div>
        ) : (
          deviceItems
        )}
        {isFetchingNextPage && (
          <div className="loading">
            <CircularProgress color="primary" />
          </div>
        )}
        <div ref={buttomRef} className="bottom" />
      </div>
      {selectedDevicesCount > 0 && (
        <div className="transfer-container">
          <span>
            {selectedDevicesCount}{' '}
            {selectedDevicesCount === 1 ? 'device' : 'devices'} selected
          </span>
          <DialogButton
            variant="contained"
            color="primary"
            onClick={() => {
              dispatch(setTransferDeviceMap(deviceMap));
              navigate('transfer-device');
            }}
          >
            Transfer
          </DialogButton>
        </div>
      )}
      <Outlet />
    </Container>
  );
};

export default LocatorPage;
